import { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronRight, BookOpen, MessageCircle, FileText, Headphones } from 'lucide-react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { SiteFiling } from '../../components/common/SiteFiling';

// 帮助中心内容
const helpContent = [
  {
    id: 1,
    category: '注册与账户',
    icon: <BookOpen className="h-5 w-5 text-teal-600" />,
    items: [
      {
        title: '如何注册账户？',
        content: `注册分五步走完，全程在同一个向导里（登录页点"立即注册"进入 /register）：
1. 阅读须知：先看注册与实名说明，点"开始注册"
2. 填写信息并验证：填用户名、真实姓名、联系电话（作为账号身份锚点）、备用电话（选填）、身份证号、邮箱；点"获取验证码"收邮箱 6 位码并填入，系统同时做"姓名+身份证+手机号"实名三要素核验
3. 系统检测：自动核对手机号/邮箱/身份证是否已被占用，据此走登录引导、身份合并、客户认领或新用户注册
4. 设置密码：填密码与确认密码，勾选同意《用户协议》《隐私政策》
5. 完成：注册成功后可直接进入平台或返回登录
注意：用户名、真实姓名、身份证号、邮箱一经注册即为身份锚点，不能自行修改；联系电话可后续在个人中心更新。`
      },
      {
        title: '忘记密码怎么办？',
        content: `重置密码走邮件链接，不是短信/邮箱验证码：
1. 在登录页面点击"忘记密码"
2. 输入注册时使用的邮箱，点"发送重置邮件"
3. 查收邮件，点邮件里的重置链接（会打开"设置新密码"页面）
4. 填写新密码与确认新密码，点"确认修改"
5. 用新密码登录
注意：重置链接是一次性令牌，只能从邮件链接进入；若没收到邮件，先查垃圾箱，仍没有请联系客服。`
      },
      {
        title: '个人信息里哪些能改、哪些不能改？如何改密码？',
        content: `进入顶部导航的"个人中心"，分"个人信息"和"账号安全"两个标签：
· 可修改（个人信息）：手机号、备用电话、所在公司、所在地区、详细地址、紧急联系人姓名与电话、个人简介，改完点"保存修改"
· 不可修改（作为实名身份锚点，页面上只读置灰）：姓名、用户名、身份证号、邮箱
· 修改登录密码（账号安全）：填"当前密码 + 新密码 + 确认新密码"，点"修改密码"，无需验证码
· 同一标签下还能管理登录设备、发起账号注销
注意：确需变更邮箱或姓名等实名信息，请联系客服走人工处理。`
      },
      {
        title: '如何注销账号？注销后数据会被删除吗？',
        content: `1. 登录平台后，进入"个人中心"，展开"注销账号"
2. 页面会先给出清单：会删除什么、会保留什么、为什么保留，看完再决定
3. 勾选「我已知晓」，输入账号密码（从未设置过密码可留空）与短信验证码，一次提交即完成
注意：系统不设冷静期，提交即生效、不可撤销，也没有"7 个工作日内删除"这类缓冲。
删除范围（只涉及不牵连他人账目的数据）：
· 本人孤立订单——仅你提交、且对方不是注册用户的订单，连同其付款凭证图片一并删除
· 孤立客户档案——你添加的、系统内已无其他用户绑定的客户
保留范围（属于双方对账凭据，注销哪一方都不动）：
· 交互订单——订单指向的客户是注册用户，双方各留一份
· 客户档案——你已被别人添加为客户时，你的手机号、邮箱作为公共客户资料保留，仅清除你的登录信息
· 备注、审核记录与操作审计
注销后可以用同一手机号或邮箱重新注册；用户名在注销时释放，未被他人占用即可继续使用。`
      }
    ]
  },
  {
    id: 2,
    category: '订单与收款',
    icon: <FileText className="h-5 w-5 text-teal-600" />,
    items: [
      {
        title: '这个平台会代收代付、或帮忙调解订单纠纷吗？',
        content: `不会。本系统只是一个"订单管理 + 对账核对"的工具平台，不碰钱、不裁断：
· 不经手资金：不提供任何收款/付款的线上交易服务，也不接入支付通道。所谓"记录收款"只是登记"谁在什么时候收了多少"用于对账，款项实际怎么走、到不到账，与平台无关
· 不介入交涉：订单是否属实、金额有无争议、双方对账不一致等，都由你们自行协商；平台只做记录、比对与留痕，不充当担保方、裁判或中间人
· 只据实记账：你录入的订单、收款、审核结果会如实保存，并可导出/分享，作为你们双方对账的凭据
正因为它只是工具、既不掌握你的资金也不代为保管凭据，请务必自己及时备份订单数据（见"数据与导出 → 如何备份数据"），重要账目不要只依赖平台。`
      },
      {
        title: '如何创建和管理订单？',
        content: `1. 登录平台后，点击导航栏的"订单管理"
2. 点击"创建订单"按钮
3. 填写订单信息，包括客户信息、服务内容、预约时间等
4. 点击"保存"按钮创建订单
5. 在订单列表和详情中可查看、编辑订单，并按流程取消订单
6. 只有你自己创建的订单能改；档期一过（已完成/已归档）或已取消的单就只读，仍可查看、记录收款、归档与导出（被对方驳回的单例外，改好后可以重新提交）
7. 订单分类创建后不可修改；订单不能删除，只能取消（删除是管理员专属操作，普通账号请联系客服）
8. 填写订单前请先与对方沟通协商：同一场活动双方各自填单，容易在对方账里留下两张重复的订单`
      },
      {
        title: '双方都提交了同一个客户、同一天的订单，怎么算？',
        content: `1. 提交时系统会先比对：客户姓名相同、预约日期相同即视为"可能是同一张订单"（两边金额填得不一样也算命中，正是要给你看的差异）
2. 命中时订单不会直接落库，页面会列出先提交的那几张单（单号、金额、谁在何时提交、当前审核状态），请你核对
3. 确认是同一张订单：到"订单审核"里驳回其中一张即可，另一张保留
4. 确认不是同一张订单（例如一天排了两场）：选择"继续提交"，这张单照常成立
5. 对方提交时系统会同步给他一条审核提醒，双方都在等同一场核对，不会出现只有一方知道的情况
注意：提示只是预防机制，不限制你一天提交多个订单；需要外调他人帮忙干活的场景，也照常提交即可`
      },
      {
        title: '订单有提醒功能吗？怎么设置？',
        content: `提醒分两层，没有"给单张订单设定提醒时间+推送方式"的开关：
1. 备注提醒：在订单详情的"备注"里给某条备注挂一个提醒日期（有"明天/3天后/1周后"快捷项，也可自选日期）。到这天它会出现在首页的"今日提醒"和订单的提醒区。
2. 系统自动通知：审核提醒、档期前 3/2/1 天的催审、补录提醒、对账轧差、会员到期等，由服务端按业务节点自动发出，不需要你手动配。
3. 通知渠道：进入"设置 → 通知设置"，可按类别开关站内、短信、邮件、浏览器推送等接收方式（这是账号级的总开关，不是针对单张订单）。`
      },
      {
        title: '如何导出订单数据？',
        content: `1. 进入"订单管理"页面
2. 用筛选条件选好需要导出的范围（时间、状态、客户等）
3. 点击"导出"，可选"导出 Excel""导出 PDF"或"打印"
4. 三种产物取数与筛选同源：Excel（.xlsx）是含订单全字段与明细的台账，金额是能直接求和的数字格、单号与电话是文本格；"导出 PDF"由服务端直接生成一份可下载的真 .pdf 文件（A4 横版、与台账同款列）；"打印"是把 12 列精简版交给浏览器打印对话框（自行选纸张、份数）
5. 都按当前筛选一次取全量，与列表页所见一致，不受分页影响
注意：导出、打印与 PDF 均为尊享会员专属功能，未开通时点击会提示升级`
      },
      {
        title: '订单状态有哪些？',
        content: `订单有两个独立维度：
· 执行状态：未执行、执行中、已完成、已取消、已归档。其中"未执行/执行中/已完成"是按档期日期由系统自动判定的，你只能手动把订单置为"已取消"或"已归档"（在订单列表切到看板视图，把订单卡片拖入对应列即可）。
· 审核状态：待审核、已通过、已拒绝。提交后需对方（或管理员）审核；被驳回的订单在改好后可重新提交。
两个维度互不替代：一张单可能"未执行 + 待审核"，也可能"已完成 + 已通过"。`
      },
      {
        title: '如何记录一笔收款？付款状态是怎么算的？',
        content: `平台不做在线收退款，只登记"谁在什么时候收了多少"用于对账：
1. 打开订单详情，在收款区新增一条收款记录（金额、日期，必要时付款方式）
2. 付款状态由累计已收金额自动判定，共三档：未付款、部分付款、已结清（已收 ≥ 订单金额即结清；付款徽章在整单没有金额时整块不显示）
注意：订单本身没有"退款"状态，也不提供退款按钮——退款只出现在会员续费/支付流水的语境里，与业务订单对账无关。`
      },
      {
        title: '如何管理服务类型？价格填在哪？',
        content: `服务类型只是一个"名字目录"，不带价格与描述：
1. 进入"设置 → 订单设置"，在"服务类型"里新增或改名（只填名称）
2. 你新建的服务类型归你私有，不会污染别人的目录
3. 金额不在目录里预设——每张订单的服务内容、单价、数量、订单总额都在填单时逐条录入
注意：给服务类型改名不会自动改写历史订单里已存的旧名，需要的话自行核对。`
      },
      {
        title: '在哪里看营收、对账与收款流水？',
        content: `没有"财务管理/账单记录"这样的独立页，财务相关看两处：
1. 顶部导航"核算管理"：月度账单、收支流水、订单轧差（抵消）等汇总与明细都在这里，可按时间范围筛选。
2. 订单详情的收款区：单张订单的每一笔收款记录与付款状态。
经营概览（收入/支出/净利、趋势）在"数据统计"页，但那是尊享会员功能。`
      },
      {
        title: '订单需要对方审核吗？怎么确认或驳回？',
        content: `提交订单后它进入"待审核"，由订单指向的对方（注册用户）或管理员来审：
1. 对方在"订单管理"顶部的"待我审核"区看到你发来的单，可"确认通过"或"拒绝"（拒绝可填原因）
2. 你这边也能看到自己发出单的审核进度；被驳回的单改好后可重新提交，且驳回会解锁"改派客户"，让你把这张单转派给另一个注册用户重走审核
3. 审核与执行状态是两回事：审核只标记"对方认不认这张单"，不改变它未执行/执行中/已完成的推进
注意：近一个月取消次数过多（默认 5 次）时，对方审核这张单时会看到一条违约风险提示。`
      },
      {
        title: '要外调人手干活，怎么记成本和预计利润？',
        content: `用订单详情里的"人员调用（支出订单）"来记，不用自己另算：
1. 在你名下的收入向、进行中的订单里添加人员：从你的客户名单选人、填角色（如摄影/助理）和酬劳
2. 每加一行，系统会自动生成一张对应的"支出订单"挂到对方名下；删掉这行，那张支出单也随之作废
3. 订单详情的核算会实时给出：订单收入 − 人员支出 = 预计利润
注意：候选人不能是这张单的服务对象、也不能是你本人；平台没有独立的"成本字段"，成本就是通过人员调用（支出订单）体现。`
      },
      {
        title: '和同一个客户既有收又有付，能相互抵消吗？',
        content: `能，用"核算管理"里的"订单抵消（轧差）"，这是尊享会员功能：
1. 按客户把你名下的收入订单与支出订单并排列出，勾选要对冲的单（可只筛未付清的）
2. 两种模式：按整单等额"订单抵消"，或按双方较小的待付额做"金额抵消"
3. 生成抵消批次后，若对方也是注册用户，需对方在他的"抵消审核"页点"同意抵消"才生效，双方账上各留一条轧差记录
注意：抵消只把应收应付对冲，不改动订单本身的收款进度。`
      }
    ]
  },
  {
    id: 3,
    category: '功能使用',
    icon: <MessageCircle className="h-5 w-5 text-teal-600" />,
    items: [
      {
        title: '如何设置和管理客户信息？',
        content: `1. 点击顶部导航的"客户管理"，再点"创建客户"
2. 填写姓名、联系方式、公司、客户类型/标签等信息并保存
3. 在客户列表可查看、编辑客户；客户类型与标签在"设置 → 客户设置"里维护（你新建的归你私有）
4. 关于"删除"：删除客户本质是解除你与它的绑定；只有当这个客户没被其他任何用户绑定、且它名下订单都归你时，才会被彻底删除，否则档案会作为公共客户资料保留
5. 客户详情页还能查看该客户的订单与核算，并生成分享核对单`
      },
      {
        title: '如何使用数据分析功能？',
        content: `1. 登录平台后，点击导航栏的"数据统计"
2. 可以查看收入/支出/净利、当月与上月对比等总览指标
3. 可以查看月度营收与订单量趋势、服务类型分布、客户排行、订单状态分布
4. 可以按时间范围筛选：预设区间或直接指定起止日期
注意：数据统计为尊享会员专属功能，未开通时该页只显示升级提示，不展示任何图表`
      },
      {
        title: '怎么把订单或客户对账单分享给对方确认？',
        content: `分享是尊享会员功能，生成的是公开链接、对方无需登录即可查看：
1. 分享单张订单：在订单详情里点"分享"，生成一张"订单服务确认单"链接
2. 分享客户对账：在客户详情页点"分享"，生成一份"客户订单核对单"，按收入/支出分列、只含未付清款项
3. 复制链接发给对方即可；随时可在原页面撤销分享
注意：分享链接实时取数、不存快照，对方打开看到的永远是最新数据；分享内容不含审核留言与收款凭证。`
      },
      {
        title: '如何补录过去的订单？',
        content: `系统默认只能新建"今天及以后"的订单，补录历史订单要先开启补录：
1. 进入"设置 → 订单设置"，在补录相关项里开启补录模式（按账号给你一个补录时间窗口）
2. 开启后即可选择过去的日期建单；补录单免审核、会单独留痕并给你发一条补录提醒
3. 补录窗口是按用户计算的有时效，过期后需重新开启
注意：不在窗口内却选了过去的日期，会提示"仅允许选择今天及以后的日期，如需补录请先开启补录模式"。`
      }
    ]
  },
  {
    id: 4,
    category: '数据与导出',
    icon: <FileText className="h-5 w-5 text-teal-600" />,
    items: [
      {
        title: '如何备份数据？',
        content: `1. 进入"设置"页面
2. 选择"数据与隐私"标签
3. 展开"数据备份"，点击"开始备份"
4. 浏览器直接下载一份 JSON 快照，文件名形如"数据备份_2026-09-10.json"
5. 快照范围固定为本人账本：我创建的订单（含回收站）及其订单项、成本、支付记录、备注、人员调用、状态与操作日志、对账明细与结算批次，以及我名下的客户与自定义类型、标签
6. 该功能为尊享会员专属；口令、身份证号等机密不会写入备份
注意：备份文件用于自行留存与迁移交接，平台暂未提供一键导入，需要回导数据请联系客服。
提醒：平台不经手你的资金、也不代为保管交易凭据，订单数据请养成实时/定期备份的习惯——尤其在批量录单后、月底对账前、账号注销前，先导出一份 JSON 自留，别把重要账目只押在线上。`
      },
      {
        title: '如何导入数据？',
        content: `平台暂未提供自助导入：服务端只有导出端点，没有对应的导入入口。
1. 需要把历史数据回导进来，请先在"设置 → 数据与隐私"导出 JSON 备份
2. 联系客服提交该备份文件，由人工核对后协助迁移
3. 订单、客户等少量数据建议直接在页面重新录入`
      },
      {
        title: '数据存储期限是多久？',
        content: `平台不按时间自动删除或归档业务数据，没有"到期清空"这类机制。
1. 删除订单是软删除（且仅管理员可执行），记录仍保留在库中，会随本人数据备份一起导出
2. 会员到期只关闭会员功能（导出、打印、分享、数据统计、备份），不会删除或隐藏你的数据
3. 如需彻底删除数据，请联系客服处理`
      },
      {
        title: '如何确保数据安全？',
        content: `我们采取以下措施确保数据安全：
1. 数据加密存储
2. 定期数据备份
3. 访问权限控制
4. 安全审计
5. 防DDoS攻击`
      },
      {
        title: '如何导出客户数据？',
        content: `1. 进入"客户管理"页面
2. 用搜索框或客户类型筛选出需要的客户
3. 点击"导出 Excel"图标，在弹出的面板里勾选要带上的可选字段（客户类型、客户姓名、联系方式为固定列，每份都带；勾选结果只对本机记住）
4. 点面板底部的"立即导出 Excel"，浏览器直接下载一份 .xlsx 文件，内容为当前筛选出的客户
注意：客户导出只有 .xlsx 一种格式（联系方式写成文本格，0 开头的座机号不会被 Excel 吃掉前导 0），且与订单导出同属尊享会员专属功能，未开通时点击会提示升级`
      }
    ]
  },
  {
    id: 5,
    category: '技术与安全',
    icon: <MessageCircle className="h-5 w-5 text-teal-600" />,
    items: [
      {
        title: '平台支持哪些浏览器？',
        content: '我们支持所有主流浏览器，包括Chrome、Firefox、Safari、Edge等。建议使用最新版本的浏览器以获得最佳体验。'
      },
      {
        title: '如何解决登录问题？',
        content: `1. 检查用户名和密码是否正确
2. 检查网络连接是否正常
3. 清除浏览器缓存和 cookies
4. 尝试使用其他浏览器
5. 如果问题仍然存在，请联系客服`
      },
      {
        title: '平台是否支持移动端？',
        content: `支持。除了手机浏览器直接访问网页版，还可以下载原生 App：
1. 打开下载页 /download（免登录），三平台并排：Android、iOS、鸿蒙
2. Android 扫码或点按钮下载自托管的安装包；iOS、鸿蒙扫码直达各自应用商店页
3. 哪个平台还没上架，整块会显示"敬请期待"
App 与网页版登录同一账号、数据互通。`
      },
      {
        title: '如何报告系统问题？',
        content: `1. 在本页点"联系客服团队"，或进入"联系我们"页
2. 在"发送消息"表单里填写姓名、邮箱、公司（选填）
3. "咨询类型"选"技术支持"
4. 在"消息内容"里详细描述问题（出现频率、操作步骤、报错提示等）
5. 点"发送消息"提交
注意：联系表单暂未提供截图/附件上传，请把关键信息写进正文，或事后通过邮件补充。`
      }
    ]
  },
  {
    id: 6,
    category: '服务与支持',
    icon: <Headphones className="h-5 w-5 text-teal-600" />,
    items: [
      {
        title: '客服工作时间是多久？',
        content: '客服工作时间为工作日 09:00-18:00；通过"联系我们"提交的留言，一般 7 个工作日内回复。'
      },
      {
        title: '如何获取技术支持？',
        content: `1. 先在本页搜索/浏览常见问题
2. 未解决就在本页点"联系客服团队"，进入"联系我们"
3. "咨询类型"选"技术支持"，详细描述问题
4. 点"发送消息"提交，客服会在工作时间跟进`
      },
      {
        title: '会员为什么要收费？费用主要花在哪儿？',
        content: `这是一套需要长期有人维护的在线系统，会员费覆盖的是"让它一直跑下去"的硬成本：
· 云服务器与带宽：订单、对账、导出、备份都要 7×24 在线，服务器和网络是持续开销
· 域名与备案：网站与 App 的域名、ICP 备案及相关合规
· 第三方通道：短信验证码、邮件、实名核验等按量计费的服务
· 持续维护与迭代：修 Bug、补功能、保障数据安全与浏览器/机型兼容，都需要投入
坦白说，国内大多数使用者还没有"为软件的长期维护付费"的习惯——很多人希望工具免费，却默认它永远不掉线、永远有新功能。我们把成本收在会员制里，让愿意为之付费的人来支撑持续维护：免费版够日常记账，会员版把导出、打印、分享、数据统计、备份等重度能力放进来，各取所需。`
      },
      {
        title: '如何升级套餐？',
        content: `1. 进入"设置"页面
2. 点击"会员特权"标签
3. 在会员状态卡片点"升级"，选择需要的套餐
4. 确认下单，按页面给出的渠道完成支付（页面跳转、扫码或转账）
5. 支付成功后系统自动激活，页面提示"支付成功！会员已激活"
6. 若支付后状态未立即变化，请稍等片刻刷新页面`
      },
      {
        title: '邀请朋友注册有会员优惠吗？',
        content: `有，入口在"设置 → 会员特权"的邀请面板（邀请减免）：
1. 面板里有你的邀请码和一条可分享的邀请链接（指向首页并带 ?ref=），一键复制发给朋友
2. 朋友通过你的链接注册并登录后即与你绑定，达成计酬条件后你获得相应的会员时长奖励
3. 下方"我的邀请记录"能看到已邀请/已计酬/累计获得月数等，只显示结果、不暴露被邀请人身份
注意：具体奖励月数与计酬条件以该页面展示的规则为准。`
      },
      {
        title: '有哪些途径可以减免会员费？',
        content: `除了直接付费升级，我们把一部分成本开放给"用贡献来抵扣"：
· 邀约减免：在"设置 → 会员特权"里复制你的邀请码或邀请链接（带 ?ref=）发给朋友，对方经链接注册并达成计酬条件后，你获得相应的会员时长奖励（详见"邀请朋友注册有会员优惠吗？"）
· 贡献思路减免：使用中若提出被我们采纳并上线的改进建议，或报出影响使用的关键缺陷，可保留记录联系客服，核实后按贡献给予一定的会员时长减免
为什么愿意这样做：这套系统的云服务器、域名、短信/邮件通道与持续维护都是真金白银的开销，而愿意帮它变得更好的人，理应比单纯付费的人拿到更多。所以能靠邀约和贡献抵扣的，我们尽量不让你全掏。
注意：贡献类减免以人工核实为准，具体幅度与规则请先联系客服确认。`
      },
      {
        title: '如何取消订阅？',
        content: `取消会员即关闭自动续费，会员权益保留到当前周期到期日。
1. 进入"设置 → 会员特权"
2. 关闭"自动续费"开关（仅付费会员可设置，免费账号后端会拒绝）
3. 到期后自动回落为免费版，只关闭会员功能，不删除任何数据`
      }
    ]
  }
];

export default function HelpPage() {
  const { siteName, logoUrl } = useSiteConfig();
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const [activeCategory, setActiveCategory] = useState<number>(1);

  // 监听滚动事件，控制导航栏样式
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleItem = (categoryId: number, itemIndex: number) => {
    const key = `${categoryId}-${itemIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* 顶部导航 */}
      <nav className={`fixed top-0 left-0 right-0 z-50 h-[70px] transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-100' : 'bg-transparent'}`}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 h-full flex items-center justify-between">
          <a href="/login" className="flex items-center gap-3">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="w-10 h-10"
            />
            <span className={`text-lg font-bold tracking-wide transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'}`}>
              {siteName}
            </span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/help" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-teal-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              帮助中心
            </a>
            <a href="/about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              关于我们
            </a>
            <a href="/contact" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              联系我们
            </a>
            <a 
              href="/login" 
              className={`text-sm font-medium px-5 py-2 rounded-full transition-all ${isScrolled ? 'bg-teal-600 text-white hover:bg-teal-700' : 'border border-white/70 text-white hover:bg-white/20'}`}
            >
              立即登录
            </a>
          </div>
        </div>
      </nav>

      {/* Hero 顶部区域 */}
      <section className="pt-[140px] pb-16 bg-gradient-to-br from-slate-800 via-slate-700 to-teal-800">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          {/* 标题 */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            常见问题解答
          </h1>

          {/* 描述文字 */}
          <p className="max-w-xl mx-auto text-sm md:text-base text-slate-300 leading-relaxed mb-8">
            收录了{helpContent.reduce((n, c) => n + c.items.length, 0)}个常见问题，覆盖注册登录、订单与收款、客户与分享、数据导出、会员等各个方面。
          </p>

          {/* 搜索框 */}
          <div className="max-w-md mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索常见问题，例如：登录、预约、密码..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-12 border border-slate-200 rounded-full text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
              />
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 主内容区 */}
      <main className="flex-1 py-14">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          {/* 面包屑导航 */}
          <div className="text-xs text-slate-500 mb-8">
            <a href="/" className="hover:text-teal-600 transition-colors">首页</a>
            <span className="mx-2">/</span>
            <span className="text-slate-700 font-semibold">帮助中心</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 左侧目录 */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sticky top-24">
                <h2 className="text-lg font-bold text-slate-800 mb-6">问题分类</h2>
                <ul className="space-y-2">
                  {helpContent.map((category) => (
                    <li key={category.id}>
                      <a 
                        href={`#category-${category.id}`} 
                        onClick={() => setActiveCategory(category.id)}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${activeCategory === category.id ? 'bg-teal-50 text-teal-600' : 'text-slate-600 hover:bg-slate-50'}`}
                      >
                        <div className="flex items-center gap-3">
                          {category.icon}
                          <span className="text-sm font-medium">{category.category}</span>
                        </div>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {category.items.length}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-slate-100">
                  <a href="/contact" className="w-full flex items-center justify-center gap-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 transition-colors py-3 rounded-lg">
                    <Headphones className="h-4 w-4" />
                    <span>联系客服团队</span>
                  </a>
                </div>
              </div>
            </div>

            {/* 右侧内容 */}
            <div className="lg:col-span-2">
              {helpContent.map((category) => (
                <div key={category.id} id={`category-${category.id}`} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-8">
                  <div className="flex items-center gap-3 mb-6">
                    {category.icon}
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-slate-800">{category.category}</h2>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {category.items.length}个常见问题
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {category.items.map((item, index) => {
                      const key = `${category.id}-${index}`;
                      const isExpanded = expandedItems[key];
                      return (
                        <div key={index} className="border border-slate-100 rounded-lg overflow-hidden">
                          <button
                            onClick={() => toggleItem(category.id, index)}
                            className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                          >
                            <span className="text-sm font-medium text-slate-800">{item.title}</span>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-500" />
                            )}
                          </button>
                          {isExpanded && (
                            <div className="p-4 border-t border-slate-100 bg-slate-50">
                              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                                {item.content}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* 底部联系卡片 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                <div className="bg-teal-50 border border-teal-100 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Headphones className="h-5 w-5 text-teal-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-teal-800">提交工单</h3>
                  </div>
                  <p className="text-xs text-teal-700 mb-3">
                    描述您的问题，一般 7 个工作日内回复
                  </p>
                  <a href="/contact" className="text-xs text-teal-600 hover:text-teal-700 transition-colors flex items-center gap-1">
                    <span>立即提交</span>
                    <ChevronRight className="h-3 w-3" />
                  </a>
                </div>
                <div className="bg-teal-50 border border-teal-100 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <MessageCircle className="h-5 w-5 text-teal-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-teal-800">预约演示</h3>
                  </div>
                  <p className="text-xs text-teal-700 mb-3">
                    专业顾问一对一演示，30分钟快速上手
                  </p>
                  <a href="/contact" className="text-xs text-teal-600 hover:text-teal-700 transition-colors flex items-center gap-1">
                    <span>预约时间</span>
                    <ChevronRight className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 底部导航 */}
      <footer className="bg-slate-800 text-white py-8 mt-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* 左侧：Logo和品牌信息 */}
            <div className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto"
              />
              <div>
                <div className="font-semibold text-lg">{siteName}</div>
                <div className="text-xs text-slate-400">让每一笔业务都有据可查</div>
              </div>
            </div>

            {/* 右侧：导航链接 */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <a href="/terms" className="hover:text-white transition-colors">服务条款</a>
              <a href="/privacy" className="hover:text-white transition-colors">隐私政策</a>
              <a href="/help" className="hover:text-white transition-colors">帮助中心</a>
              <a href="/contact" className="hover:text-white transition-colors">联系我们</a>
              <a href="/about" className="hover:text-white transition-colors">关于我们</a>
            </div>
          </div>

          {/* 底部备案信息 */}
          <div className="border-t border-slate-700 mt-8 pt-6 text-center text-xs text-slate-500">
            <SiteFiling />
          </div>
        </div>
      </footer>
    </div>
  );
}
